import icons from '../icons/icons'
import { FunctionComponent, SVGProps } from 'react'
import { Editor } from '@tiptap/core'
import { ControlProps, CSSObjectWithLabel, MultiValue, OptionProps, SingleValue } from 'react-select'
import { fontList, format, getFontSize, Format, getLevel, getHeading, getFormatLabel } from '../fonts/index'
import "../fonts/delicious-handrawn/styles.scss"
import "../fonts/noto-serif/styles.scss"
import "../fonts/roboto/styles.scss"
import "../fonts/ubuntu/styles.scss"

export type ButtonProps = {
    id: string
    name: string
    type: 'button'
    hide?: boolean
    Icon: FunctionComponent<SVGProps<SVGSVGElement>>
    extraUi?: {
        color?: true
    },
    className?: (editor: Editor) => string,
    onClick: (editor: Editor, value?: string) => void
}

export type RadioButtonProps = Omit<ButtonProps, "type"> & { type: "radioButton" }

export type SelectOption = {
    value: string
    label: string
}

export type SelectProps = {
    id: string
    name: string
    type: 'select'
    hide?: boolean
    values: SelectOption[]
    getDefaultValue?: (editor: Editor) => SelectOption|undefined|null
    controlStyles?: (e: CSSObjectWithLabel, props: ControlProps<SelectOption>) => CSSObjectWithLabel
    optionStyles?: (e: CSSObjectWithLabel, props: OptionProps<SelectOption>) => CSSObjectWithLabel
    onChange?: (editor: Editor, value: SingleValue<SelectOption> | MultiValue<SelectOption>) => void
}

type Space = {
    type: 'space'
    hide?: boolean
}

export type MenuItemProps = ButtonProps | RadioButtonProps | SelectProps | Space

const menu : MenuItemProps[] = [{
    id: "font",
    name: "Font",
    type: 'select',
    hide: true,
    values: fontList.map(f => { return { value: f.id, label: f.name } }),
    controlStyles: (styles, props) => { 
        const customStyles : CSSObjectWithLabel = {}
        if (props.getValue().length) {
            customStyles.fontFamily = props.getValue()[0].label
        }
        return {
            ...styles,
            ...customStyles
        }
    },
    optionStyles: (styles, { data }) => {
        return {
            ...styles,
            fontFamily: data.label
        }
    },
    onChange: (editor, value) => {
        const font = value as SelectOption
        editor.chain().focus().setFontFamily(font.label).run()
    },
    getDefaultValue: (editor) => {

        if (!editor) {
            return null
        }

        const textStyle = editor.getAttributes('textStyle')

        if (textStyle.fontFamily) {
            const font = fontList.find(f => f.name === textStyle.fontFamily)
            return font ? {
                value: font.id,
                label: font.name
            } : null
        }
        return null
    }
}, {
    type: 'space',
    hide: true
}, {
    type: 'select',
    id: 'heading',
    name: 'Estil del text',
    values: format.map(f => { return { label: getFormatLabel(f), value: f}}),
    optionStyles: (styles, { data }) => { 
        return {
            ...styles,
            fontSize: getFontSize(data.value as Format)
        }
    },
    onChange: (editor, value) => {
        const format = (value as SelectOption).value as Format
        if(format === Format.Normal) {
            editor.chain().focus().setParagraph().run()

        } else {
            editor.chain().focus().setHeading({ level: getLevel(format)}).run()
        }
    },
    getDefaultValue: (editor) => {

        if (!editor) {
            return null
        }

        const heading = editor.getAttributes('heading')

        if (heading.level) {
            const format = getHeading(heading.level)
            return {
                value: format,
                label: getFormatLabel(format)
            }
        } else {
            return {
                value: Format.Normal,
                label: getFormatLabel(Format.Normal)
            }
        }
    }

}, {
    type: 'space'
}, {
    id: "bold",
    name: "Negreta",
    type: 'button',
    Icon: icons.bold,
    onClick: (editor) => {  editor.chain().focus().toggleBold().run() }
},{
    id: "italic",
    name: "Cursiva",
    type: 'button',
    Icon: icons.italic,
    onClick: (editor) => {  editor.chain().focus().toggleItalic().run() }
},{
    id: "underline",
    name: "Subratllat",
    type: 'button',
    Icon: icons.underline,
    onClick: (editor) => {  editor.chain().focus().toggleUnderline().run() }
},{
    id: "strike",
    name: "Ratllat",
    type: 'button',
    Icon: icons.strike,
    onClick: (editor) => {  editor.chain().focus().toggleStrike().run() }
}, {
    type: 'space'
}, {
    id: "blockquote",
    name: "Citació",
    type: 'button',
    Icon: icons.blockquote,
    onClick: (editor) => {  editor.chain().focus().toggleBlockquote().run() }
},{
    id: "code",
    name: "Codi",
    type: 'button',
    Icon: icons.code,
    onClick: (editor) => {  editor.chain().focus().toggleCode().run() }
}, {
    type: 'space'
}, {
    id: "orderedList",
    name: "Llista ordenada",
    type: 'button',
    Icon: icons.list.ordered,
    onClick: (editor) => {  editor.chain().focus().toggleOrderedList().run() }
},{
    id: "unorderedList",
    name: "Llista no ordenada",
    type: 'button',
    Icon: icons.list.bullet,
    onClick: (editor) => {  editor.chain().focus().toggleBulletList().run() }
},{
    type: 'space'
},{
    id: "alignLeft",
    name: "Alinea a l'esquerra",
    type: 'radioButton',
    Icon: icons.align.left,
    className: ( editor ) => { return cssAlign(editor, 'left')},
    onClick: ( editor ) => { alignHandler(editor, 'left') }
},{
    id: "alignCenter",
    name: "Alinea al centre",
    type: 'radioButton',
    Icon: icons.align.center,
    className: ( editor ) => { return cssAlign(editor, 'center')},
    onClick: ( editor ) => { alignHandler(editor, 'center') }
},{
    id: "alignRight",
    name: "Alinea a la dreta",
    type: 'radioButton',
    Icon: icons.align.right,
    className: ( editor ) => { return cssAlign(editor, 'right')},
    onClick: ( editor ) => { alignHandler(editor, 'right') }
},{
    id: "alignJustify",
    name: "Justifica",
    type: 'radioButton',
    Icon: icons.align.justify,
    className: ( editor ) => { return cssAlign(editor, 'justify')},
    onClick: ( editor ) => { alignHandler(editor, 'justify') }
},{
    type: 'space'
}, {
    id: "color",
    name: "Color",
    type: 'button',
    Icon: icons.color,
    extraUi: { color: true },
    onClick: ( editor, value ) => { if (value) { editor.chain().focus().setColor(value).run() } }
},{
    id: "backgroundColor",
    name: "Color de fons",
    type: 'button',
    Icon: icons.background,
    extraUi: { color: true },
    onClick: ( editor, value ) => { if (value) { editor.chain().focus().setBackgroundColor(value).run() } }
},{
    type: 'space'
}, {
    id: 'link',
    name: 'Enllaç',
    type: 'button',
    Icon: icons.link,
    onClick: () => {}
}, {
    id: 'clean',
    name: 'Neteja el format',
    type: 'button',
    Icon: icons.clean,
    onClick: ( editor ) => { 
        editor.chain().focus().clearNodes().unsetAllMarks().run() 
    }
}]

const isActiveAlign = (editor: Editor, align: string) => {
    return editor.isActive({ textAlign: align })
}

const alignHandler = (editor: Editor, align: string) => { 
    isActiveAlign(editor, align) ?
        editor.chain().focus().unsetTextAlign().run() :
        editor.chain().focus().setTextAlign(align).run()
}

const cssAlign = (editor: Editor, align: string) => {
    return isActiveAlign(editor, align) ? 'is-active' : ''
}

export default menu;

